import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IEffortTeamResponse } from '../../../leopart-update/components/effort/effort.types';
import { normalizeTeamEffort } from '../../../leopart-update/components/effort/useTeamEffort';
import { useTeamRecovery } from '../../../leopart-update/components/effort/useTeamRecovery';
import { addDays, toIsoDate } from '../allenamenti.constants';

/** RPE medio squadra e recupero (RI) medio squadra di UN giorno. */
export interface ITeamDailyLoadRecovery {
  /** RPE medio 1-10, media dei `rpeAvg` giornalieri di tutti gli atleti — null se nessun dato. */
  avgRpe: number | null;
  /** RI medio 0-100, media dei RI giornalieri di tutti gli atleti — null se nessun dato. */
  avgRi: number | null;
}

/** `YYYY-MM-DD` → `Date` locale, senza passare per `Date.parse` (che tratta le
 * date "sole" come UTC e sposta di un giorno nei fusi negativi — stesso motivo
 * per cui `allenamenti.constants.ts` evita `toISOString`). */
function parseIsoDateLocal(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/**
 * RPE medio e recupero medio della SQUADRA, per ogni giorno della settimana
 * Allenamenti (`weekStartIso` = lunedì, stessa settimana lun-dom di
 * `useAllenamentiWeek`). Sono dati di giorno+squadra, non del singolo blocco:
 * la vista settimana li mostra una volta per colonna, non ripetuti sotto ogni
 * allenamento (design condiviso con l'utente).
 *
 * Nessun endpoint nuovo: riusa `GET /effort/team/:teamId?week=` (stessa
 * chiave di cache di `useTeamEffort` — condivide la cache se il tab Sforzo è
 * stato aperto di recente) per l'RPE, e `useTeamRecovery` per l'RI, invece di
 * duplicarne la logica di fetch.
 */
export function useTeamDailyLoadRecovery(
  teamId: string,
  weekStartIso: string,
  enabled: boolean,
): Map<string, ITeamDailyLoadRecovery> {
  const endpoints = useEndpoints();

  // Stessa chiave di cache di useTeamEffort → stesso queryFn normalizzato, così
  // chi popola per primo la cache non cambia la forma letta dall'altro.
  const effortQuery = useQuery<IEffortTeamResponse>({
    queryKey: ['team-effort', teamId, weekStartIso],
    queryFn: async () => {
      const { data } = await endpoints.getTeamEffort(teamId, { week: weekStartIso });
      return normalizeTeamEffort(data);
    },
    enabled: enabled && !!teamId,
    staleTime: 1000 * 60 * 5,
  });

  const { days: recoveryDays, rows: recoveryRows } = useTeamRecovery(teamId, weekStartIso, enabled);

  return useMemo(() => {
    const weekDates = Array.from({ length: 7 }, (_, i) =>
      toIsoDate(addDays(parseIsoDateLocal(weekStartIso), i)),
    );

    const map = new Map<string, ITeamDailyLoadRecovery>();
    for (const iso of weekDates) map.set(iso, { avgRpe: null, avgRi: null });

    // RPE medio: media dei `rpeAvg` giornalieri di tutti gli atleti, per data.
    const rpeByDate = new Map<string, number[]>();
    for (const row of effortQuery.data?.rows ?? []) {
      for (const day of row.days ?? []) {
        if (day.rpeAvg == null) continue;
        const list = rpeByDate.get(day.date) ?? [];
        list.push(day.rpeAvg);
        rpeByDate.set(day.date, list);
      }
    }
    for (const [date, values] of rpeByDate) {
      const entry = map.get(date);
      if (!entry) continue;
      const avg = average(values);
      if (avg != null) entry.avgRpe = Math.round(avg * 10) / 10;
    }

    // RI medio: media dei RI giornalieri di tutti gli atleti, allineata per
    // indice a `recoveryDays` (stesso contratto di useTeamRecovery/ITeamRecoveryRow).
    recoveryDays.forEach((date, i) => {
      const entry = map.get(date);
      if (!entry) return;
      const values = recoveryRows
        .map((r) => r.days[i])
        .filter((v): v is number => v != null);
      const avg = average(values);
      if (avg != null) entry.avgRi = Math.round(avg);
    });

    return map;
  }, [effortQuery.data, recoveryDays, recoveryRows, weekStartIso]);
}
